import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import type {
  AdminV2ToolDraft,
  AdminV2WorkspaceRouteTemplatePickerDraft,
  AdminV2WorkspaceTemplatePickerDraft,
} from './tool-contract';
import type {
  AdminV2ToolRuntimePackage,
  AdminV2WorkspaceRouteTemplatePickerRuntime,
  AdminV2WorkspaceTemplatePickerRuntime,
} from './tool-types';

export const ADMIN_V2_TOOL_TEMPLATE_MEDIA_STORAGE_PREFIX =
  'cms/v2/tool-template-media';

export function getAdminV2ToolTemplateMediaStoragePrefix(toolId: string) {
  return `${ADMIN_V2_TOOL_TEMPLATE_MEDIA_STORAGE_PREFIX}/${toolId}`;
}

export function isAdminV2ToolTemplateMediaStorageKey(params: {
  toolId: string;
  storageKey: string;
  mimeType: 'image/webp' | 'image/gif';
}) {
  const extension = params.mimeType === 'image/webp' ? '.webp' : '.gif';
  return (
    params.storageKey.startsWith(
      `${getAdminV2ToolTemplateMediaStoragePrefix(params.toolId)}/`
    ) && params.storageKey.toLowerCase().endsWith(extension)
  );
}

export type AdminV2ToolTemplatePreviewReference = {
  groupId: string;
  value: string;
  storageKey: string;
  mimeType: 'image/webp' | 'image/gif';
  width: number;
  height: number;
  fieldPath: string;
};

export function listAdminV2ToolTemplatePreviewReferences(
  draft: Pick<AdminV2ToolDraft, 'workspaceView'>
): AdminV2ToolTemplatePreviewReference[] {
  return [
    ...(draft.workspaceView?.templatePicker?.groups ?? []).flatMap(
      (group, groupIndex) =>
        group.templates.flatMap((template, templateIndex) => [
          {
            groupId: group.id,
            value: template.value,
            ...template.preview,
            fieldPath: `workspaceView.templatePicker.groups[${groupIndex}].templates[${templateIndex}].preview`,
          },
          ...(template.animatedPreview
            ? [
                {
                  groupId: group.id,
                  value: template.value,
                  ...template.animatedPreview,
                  fieldPath: `workspaceView.templatePicker.groups[${groupIndex}].templates[${templateIndex}].animatedPreview`,
                },
              ]
            : []),
        ])
    ),
    ...(draft.workspaceView?.routeTemplatePicker?.templates ?? []).map(
      (template, templateIndex) => ({
        groupId: 'route',
        value: template.value,
        ...template.preview,
        fieldPath: `workspaceView.routeTemplatePicker.templates[${templateIndex}].preview`,
      })
    ),
  ];
}

export function buildAdminV2WorkspaceRouteTemplatePickerRuntime(params: {
  routeTemplatePicker?: AdminV2WorkspaceRouteTemplatePickerDraft;
  toolFields: Array<{
    id: string;
    options?: Array<{ value: string | number | boolean }>;
  }>;
}): AdminV2WorkspaceRouteTemplatePickerRuntime | undefined {
  if (!params.routeTemplatePicker) return undefined;

  const field = params.toolFields.find(
    (candidate) => candidate.id === params.routeTemplatePicker?.selectionFieldId
  );
  const templateByValue = new Map(
    params.routeTemplatePicker.templates.map((template) => [
      template.value,
      template,
    ])
  );
  return {
    selectionFieldId: params.routeTemplatePicker.selectionFieldId,
    templates: (field?.options ?? []).flatMap((option) => {
      if (typeof option.value !== 'string') return [];
      const template = templateByValue.get(option.value);
      return template
        ? [
            {
              value: option.value,
              preview: structuredClone(template.preview),
            },
          ]
        : [];
    }),
  };
}

export function toAdminV2WorkspaceRouteTemplatePickerDraft(
  runtime: AdminV2WorkspaceRouteTemplatePickerRuntime | undefined
): AdminV2WorkspaceRouteTemplatePickerDraft | undefined {
  if (!runtime) return undefined;
  return {
    selectionFieldId: runtime.selectionFieldId,
    templates: runtime.templates.map((template) => ({
      value: template.value,
      preview: structuredClone(template.preview),
    })),
  };
}

export function validateAdminV2WorkspaceRouteTemplateSelection(params: {
  runtimePackage: {
    toolFields: AdminV2ToolRuntimePackage['toolFields'];
    workspaceView: {
      routeTemplatePicker?: AdminV2WorkspaceRouteTemplatePickerRuntime;
    };
  };
  normalizedInput: Record<string, unknown>;
}) {
  const picker = params.runtimePackage.workspaceView.routeTemplatePicker;
  if (!picker) return;

  const value = params.normalizedInput[picker.selectionFieldId];
  const field = params.runtimePackage.toolFields.find(
    (candidate) => candidate.id === picker.selectionFieldId
  );
  const option = field?.options?.find(
    (candidate) =>
      typeof candidate.value === 'string' && candidate.value === value
  );
  if (
    typeof value !== 'string' ||
    !option ||
    option.hidden ||
    option.disabled ||
    !picker.templates.some((template) => template.value === value)
  ) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_ROUTE_TEMPLATE_SELECTION_INVALID',
      message:
        'Workspace route template selection is required and must be available.',
      fieldPath: `input.${picker.selectionFieldId}`,
      status: 422,
    });
  }
}

export function buildAdminV2WorkspaceTemplatePickerRuntime(params: {
  templatePicker?: AdminV2WorkspaceTemplatePickerDraft;
  toolFields: Array<{
    id: string;
    options?: Array<{ value: string | number | boolean }>;
  }>;
}): AdminV2WorkspaceTemplatePickerRuntime | undefined {
  if (!params.templatePicker) return undefined;

  return {
    basePrompt: params.templatePicker.basePrompt,
    groups: params.templatePicker.groups.map((group) => {
      const field = params.toolFields.find(
        (candidate) => candidate.id === group.selectionFieldId
      );
      const templateByValue = new Map(
        group.templates.map((template) => [template.value, template])
      );
      const templates = (field?.options ?? []).flatMap((option) => {
        if (typeof option.value !== 'string') return [];
        const template = templateByValue.get(option.value);
        return template
          ? [
              {
                value: option.value,
                preview: structuredClone(template.preview),
                ...(template.animatedPreview
                  ? {
                      animatedPreview: structuredClone(
                        template.animatedPreview
                      ),
                    }
                  : {}),
              },
            ]
          : [];
      });

      return {
        id: group.id,
        required: group.required,
        selectionFieldId: group.selectionFieldId,
        ...(group.customImageFieldId
          ? { customImageFieldId: group.customImageFieldId }
          : {}),
        ...(group.customPrompt ? { customPrompt: group.customPrompt } : {}),
        templates,
        promptsByValue: Object.fromEntries(
          group.templates.map((template) => [template.value, template.prompt])
        ),
      };
    }),
  };
}

export function toAdminV2WorkspaceTemplatePickerDraft(
  runtime: AdminV2WorkspaceTemplatePickerRuntime | undefined
): AdminV2WorkspaceTemplatePickerDraft | undefined {
  if (!runtime) return undefined;
  return {
    basePrompt: runtime.basePrompt,
    groups: runtime.groups.map((group) => ({
      id: group.id,
      required: group.required,
      selectionFieldId: group.selectionFieldId,
      ...(group.customImageFieldId
        ? { customImageFieldId: group.customImageFieldId }
        : {}),
      ...(group.customPrompt ? { customPrompt: group.customPrompt } : {}),
      templates: group.templates.map((template) => ({
        value: template.value,
        preview: structuredClone(template.preview),
        ...(template.animatedPreview
          ? {
              animatedPreview: structuredClone(template.animatedPreview),
            }
          : {}),
        prompt: group.promptsByValue[template.value] ?? '',
      })),
    })),
  };
}

function hasTemplateSelectionValue(value: unknown) {
  return value !== undefined && value !== null && value !== '';
}

export type AdminV2WorkspaceTemplatePickerComposition = {
  prompt: string;
  referenceImageUrls: string[];
};

type AdminV2WorkspaceTemplatePickerRuntimeSource = {
  toolFields: Array<
    Pick<AdminV2ToolRuntimePackage['toolFields'][number], 'id' | 'options'>
  >;
  workspaceView: {
    templatePicker?: AdminV2WorkspaceTemplatePickerRuntime;
  };
};

export function composeAdminV2WorkspaceTemplatePicker(params: {
  runtimePackage: AdminV2WorkspaceTemplatePickerRuntimeSource;
  normalizedInput: Record<string, unknown>;
}): AdminV2WorkspaceTemplatePickerComposition {
  const picker = params.runtimePackage.workspaceView.templatePicker;
  if (!picker) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_TEMPLATE_PICKER_NOT_CONFIGURED',
      message: 'Workspace template picker is not configured for this Tool.',
      fieldPath: 'workspaceView.templatePicker',
      status: 409,
    });
  }

  const fragments = [picker.basePrompt];
  const referenceImageUrls: string[] = [];

  for (const group of picker.groups) {
    const officialValue = params.normalizedInput[group.selectionFieldId];
    const customValue = group.customImageFieldId
      ? params.normalizedInput[group.customImageFieldId]
      : undefined;
    const hasOfficial = hasTemplateSelectionValue(officialValue);
    const hasCustom = hasTemplateSelectionValue(customValue);

    if (hasOfficial && hasCustom) {
      throw createAdminV2Error({
        code: 'ADMIN_V2_TOOL_TEMPLATE_SELECTION_CONFLICT',
        message: `Workspace template picker group ${group.id} cannot select both an official and custom template.`,
        fieldPath: `input.${group.selectionFieldId}`,
        status: 422,
      });
    }

    if (!hasOfficial && !hasCustom) {
      if (group.required) {
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_TEMPLATE_SELECTION_REQUIRED',
          message: `Workspace template picker group ${group.id} requires a selection.`,
          fieldPath: `input.${group.selectionFieldId}`,
          status: 422,
        });
      }
      continue;
    }

    if (hasOfficial) {
      if (typeof officialValue !== 'string') {
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_TEMPLATE_OPTION_INVALID',
          message: `Workspace template picker group ${group.id} requires a string option id.`,
          fieldPath: `input.${group.selectionFieldId}`,
          status: 422,
        });
      }
      const field = params.runtimePackage.toolFields.find(
        (candidate) => candidate.id === group.selectionFieldId
      );
      const option = field?.options?.find(
        (candidate) =>
          typeof candidate.value === 'string' &&
          candidate.value === officialValue
      );
      const fragment = group.promptsByValue[officialValue];
      if (!option || option.hidden || option.disabled || !fragment) {
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_TEMPLATE_OPTION_INVALID',
          message: `Workspace template picker group ${group.id} option is not available.`,
          fieldPath: `input.${group.selectionFieldId}`,
          status: 422,
        });
      }
      fragments.push(fragment);
      continue;
    }

    if (
      !group.customImageFieldId ||
      !group.customPrompt ||
      typeof customValue !== 'string' ||
      customValue.trim().length === 0
    ) {
      throw createAdminV2Error({
        code: 'ADMIN_V2_TOOL_TEMPLATE_CUSTOM_IMAGE_INVALID',
        message: `Workspace template picker group ${group.id} custom image is invalid.`,
        fieldPath: `input.${group.customImageFieldId ?? group.selectionFieldId}`,
        status: 422,
      });
    }
    referenceImageUrls.push(customValue);
    fragments.push(
      `${group.customPrompt} Reference image ${referenceImageUrls.length}.`
    );
  }

  return {
    prompt: fragments
      .map((fragment) => fragment.trim())
      .filter(Boolean)
      .join('\n'),
    referenceImageUrls,
  };
}

export function composeAdminV2WorkspaceTemplatePickerDraftPreview(params: {
  draft: Pick<AdminV2ToolDraft, 'toolFields' | 'workspaceView'>;
  input: Record<string, unknown>;
}) {
  const templatePicker = buildAdminV2WorkspaceTemplatePickerRuntime({
    templatePicker: params.draft.workspaceView?.templatePicker,
    toolFields: params.draft.toolFields,
  });
  return composeAdminV2WorkspaceTemplatePicker({
    runtimePackage: {
      toolFields: params.draft.toolFields,
      workspaceView: { templatePicker },
    },
    normalizedInput: params.input,
  });
}
